// ─────────────────────────────────────────────
//  Team directory domain — who may open it, and what a typed query matches.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────
//
//  The DECISION layer of the Team page: the access gate, the search, and the
//  bucketing of contact rows into sections. The row SHAPE — turning `staff`,
//  `surgeons` and `facilities` into one array, suppressing synthesized email
//  addresses, honouring `listed: false` — lives one module down in
//  domain/teamContacts.js. Both are framework-free and tested, so the page can
//  stay a thin renderer.
//
//  ⚠️ THE SHEET IS GONE (2026-08-19, owner: "I want nothing public"). This page
//  used to render ONE document mirrored weekly out of a world-readable Google
//  Sheet. It now reads three of ATC's own collections. Two consequences show up
//  in this file: there is no "last synced" line to write any more (nothing
//  syncs; an admin edits and the listener delivers), and the CRNA-vs-AA split
//  is REAL rather than one honest "CRNAs & AAs" heading over an
//  undifferentiated block the sheet could not resolve.
//
//  ⚠️ A ROW NOW CARRIES AN EMAIL, AND THAT IS A REVERSAL. Until the owner
//  changed it, email was deliberately absent from this surface. It is shown
//  now — but only a REAL one; see teamContacts.contactEmail.
// ─────────────────────────────────────────────
import { isSandboxBoot } from "../sandbox/mode";
import { SANDBOX_FACILITY_ID } from "../config/roles";
import {
  CONTACT_SECTIONS, FILTERABLE_SECTION_IDS, SECTION_LABEL, foldText, digitsOf,
} from "./teamContacts";

// Re-exported so every caller that has always imported these from here keeps
// working; they are defined in teamContacts because that is the lower module.
export { CONTACT_SECTIONS, FILTERABLE_SECTION_IDS, SECTION_LABEL, foldText, digitsOf };

// ── Who may open the directory at all ─────────
// The ONE definition of the access rule, imported by the landing door, the nav
// entry, the page body and the native landing. Four copies of a role predicate
// is how a door starts leading to a permission error.
//
// ⚠️ IT LIVES HERE, NOT IN HomePage, ON PURPOSE. HomePage imports NavMenu, so a
// helper exported from HomePage and imported by NavMenu is a circular import —
// it happens to work today because the call is at render time, and it stops
// working the moment someone reads it at module scope. This module sits below
// every consumer, so there is no cycle to get wrong.
//
// ⚠️ WRITTEN AS A POSITIVE ALLOWLIST, NEVER AS `!isObserver`. The directory is
// the personal mobile number and the inbox of everyone in the department. A
// negative check silently hands the door to every role invented after it was
// written; this one hands it to nobody who was not named. Firestore rules
// enforce the same allowlist independently — this gate exists so the UI never
// offers a door that would not open.
//
// The roles: providers (CRNAs, AAs, residents), anesthesiologists (an AIC is a
// doctor with isAIC, so already covered) and admins. Observers, schedulers and
// the unattended display accounts are OUT.
//
// ⚠️ THE ROLE IS ONLY HALF THE TEST. The account must ALSO be at one of this
// tenant's hospitals. A low-privilege profile is self-provisioned on first
// sign-in with `facilityIds: []` (buildDefaultProfile in config/roles.js, and
// isSafeSelfProfile in firestore.rules pins it), and an administrator grants
// campus membership afterwards — so an account can hold the allowlisted role
// `provider` and belong to no hospital at all. On the role alone, that account
// would be handed the whole department's contact details. It is refused here,
// and refused again by the Firestore rules, which state the identical two-part
// test.
//
// ⚠️ THE THREE IDS BELOW ARE TENANT-SPECIFIC DEBT, ON PURPOSE. This product is
// heading multi-tenant and a hardcoded facility list is exactly the kind of
// assumption that has to be generalised later — but for an access decision,
// explicit and auditable beats derived. THE LIST IS EXHAUSTIVE BY INTENT: it is
// every campus this tenant has, not a sample. A SECOND TENANT must replace it
// with its own ids here AND in firestore.rules, or replace both with a
// per-tenant config. The two must never disagree: the server is what protects
// the data, and this gate exists only so the UI never offers a door that would
// not open.
//
//   default      AdventHealth Wesley Chapel
//   carrollwood  AdventHealth Carrollwood
//   tampa        AdventHealth Tampa
export const TEAM_DIRECTORY_FACILITY_IDS = ["default", "carrollwood", "tampa"];

/**
 * The campuses THIS BOOT admits (contract §7, "environment-aware").
 *
 * ⚠️ THE SANDBOX IS A DIFFERENT DATA PLANE, NOT A LOOSER RULE. A sandbox boot
 * talks to a local store (sandbox/localDb.js); there is no server and there are
 * no security rules, because there is nothing to protect — the stranger
 * evaluating ATC on their own phone seeded every record in it themselves. So
 * the sandbox facility is admitted THERE and only there.
 *
 * ⚠️ IT IS NOT SIMPLY ADDED TO THE LIST ABOVE. An account in a LIVE tenant that
 * somehow carried `facilityIds: ["sbx"]` would then be shown a door the
 * Firestore rules refuse — the exact "door onto a permission error" this gate
 * exists to prevent. Boot mode is the discriminator, and it is read at call
 * time so a test never has to reload a module to change it.
 */
export function allowedFacilityIds() {
  return isSandboxBoot()
    ? [...TEAM_DIRECTORY_FACILITY_IDS, SANDBOX_FACILITY_ID]
    : TEAM_DIRECTORY_FACILITY_IDS;
}

export function canSeeTeamDirectory(auth) {
  if (!auth) return false;
  if (!(auth.isAdmin || auth.isDoctor || auth.isProvider)) return false;
  // The effective profile from useAuth(). A missing, empty or foreign
  // facilityIds fails CLOSED — an account nobody has placed at a hospital yet
  // is not a clinician who needs the department's contact details.
  const facilityIds = auth.user && auth.user.facilityIds;
  const allowed = allowedFacilityIds();
  return Array.isArray(facilityIds)
    && facilityIds.some((id) => allowed.includes(id));
}

// ── Search ────────────────────────────────────
// A query made only of digits and phone punctuation is a NUMBER search;
// anything with a letter in it is a NAME-or-EMAIL search. Splitting on the
// query's own shape is what lets "8137" find a number and "larson" find a
// person without the caller choosing a mode.
const PHONEY = /^[\d\s()+.\-–—]+$/;

/**
 * Does this row answer the typed query?
 * Matches, case- and accent-insensitively: the display name, the last name
 * alone, the first name alone, both name orders, the EMAIL ADDRESS as text,
 * and — for a numeric query — the digits of the phone number (formatting
 * stripped from both sides). An empty query matches everything.
 *
 * ⚠️ THE EMAIL MATCHED IS THE ONE ON THE ROW, WHICH IS THE ONE ON SCREEN. A
 * synthesized address is stripped by teamContacts.contactEmail before a row is
 * built, so typing it finds nobody — a search that hits a person through an
 * address the page refuses to print would be a way to confirm the address
 * exists.
 */
export function matchesQuery(row, query) {
  const q = String(query || "").trim();
  if (!q) return true;
  if (!row) return false;

  const digits = digitsOf(q);
  if (digits && PHONEY.test(q)) return digitsOf(row.phone).includes(digits);

  const needle = foldText(q);
  if (!needle) return false;
  const first = String(row.firstName || "");
  const last = String(row.lastName || "");
  return [row.name, first, last, `${first} ${last}`, `${last} ${first}`, row.email]
    .some((field) => foldText(field).includes(needle));
}

// ── Sectioning ────────────────────────────────
/**
 * Apply the query and the section-chip filter, then bucket what survives.
 * @param {object[]} rows      from teamContacts.buildContacts
 * @param {object}   opts
 * @param {string}   opts.query    the applied (debounced) search text
 * @param {Set|null} opts.sections the pressed chips; empty means NO filter
 * @returns {{sections: {id,label,rows}[], shown: number, total: number}}
 *   `sections` holds only NON-EMPTY buckets, in CONTACT_SECTIONS order.
 *   `shown` counts everything rendered, `total` everything there is.
 */
export function filterDirectory(rows, { query = "", sections = null } = {}) {
  const list = Array.isArray(rows) ? rows : [];
  // An empty chip set means "no filter", not "nothing" — a page that hides
  // everyone until a chip is pressed reads as broken.
  const active = sections && sections.size > 0 ? sections : null;
  const buckets = new Map(CONTACT_SECTIONS.map((s) => [s.id, []]));
  let shown = 0;

  for (const row of list) {
    if (!row) continue;
    const bucket = buckets.get(row.sectionId);
    if (!bucket) continue;
    if (active && !active.has(row.sectionId)) continue;
    if (!matchesQuery(row, query)) continue;
    shown += 1;
    bucket.push(row);
  }

  return {
    sections: CONTACT_SECTIONS
      .map((s) => ({ id: s.id, label: s.label, rows: buckets.get(s.id) }))
      .filter((s) => s.rows.length > 0),
    shown,
    total: list.length,
  };
}

/**
 * The "N people" line's denominator.
 *
 * ⚠️ NEVER HARDCODE A HEADCOUNT. It changes whenever the department hires, and
 * a denominator that disagrees with the list it describes is a quiet lie. The
 * array on screen is the only thing that can be trusted to count itself.
 */
export function directoryTotal(rows) {
  return Array.isArray(rows) ? rows.length : 0;
}

/**
 * The count line, in words. "entries" rather than "people" because this page
 * lists buildings and surgeons as well as colleagues, and "473 people" over a
 * list that includes three hospitals is wrong in a way nobody would report.
 */
export function countLabel(shown, total) {
  if (total === 0) return "";
  const noun = total === 1 ? "entry" : "entries";
  return shown === total ? `${total} ${noun}` : `${shown} of ${total} ${noun}`;
}
